import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import classnames from 'classnames';

import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import CardActions from '@mui/material/CardActions';
import Grid from '@mui/material/Grid';

import { formatValue } from '@ts-core/utils/dataTableUtils';

import Typography from '@ts-core/components/Typography';

import AssignmentListCardActions from './AssignmentListCardActions';

const useStyles = makeStyles((theme) => ({
  header: {
    backgroundColor: '#617984',
  },
  headerLoading: {
    backgroundColor: theme.palette.grey[200],
  },
  content: {
    minHeight: 100,
  },
  rightBorder: {
    borderRight: '1px solid black',
  },
  actions: {
    minHeight: 62,
  },
}));

const AssignmentListCard = (props) => {
  const classes = useStyles();

  const {
    assignment,
    loading,
    onSelectAssignment,
    onToggleAssignment,
    onRemoveAssignment,
    onEditAssignment,
    onSendAssignment,
  } = props;

  const { name, expirationDate } = assignment;
  const { stats = {} } = assignment;
  const { maxScore = 0, averageScore = 0 } = stats;

  const handleSelectAssignment = useCallback(() => {
    onSelectAssignment(assignment);
  }, [onSelectAssignment, assignment]);

  return (
    <Card variant={loading ? 'outlined' : 'elevation'}>
      <CardActionArea
        component="div"
        onClick={handleSelectAssignment}
        disableRipple
        disabled={loading}
      >
        <Box
          className={classnames(classes.header, {
            [classes.headerLoading]: loading,
          })}
          color="white"
          display="flex"
          alignItems="flex-start"
          p={2}
          height="100%"
        >
          <Box
            flexGrow={1}
            minHeight={80}
            display="flex"
            height="100%"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Typography
              variant="h6"
              color="inherit"
              loading={loading}
              skeletonWidth={100}
            >
              {name}
            </Typography>
            <Typography
              variant="body2"
              color="inherit"
              loading={loading}
              skeletonWidth={60}
            >
              <FormattedMessage
                id="assignment.expiration"
                values={{
                  date: moment(expirationDate).format('DD/MM/YYYY HH:mm'),
                }}
              />
            </Typography>
          </Box>
        </Box>
        {assignment.sent && (
          <CardContent className={classes.content}>
            <Grid container>
              <Grid item xs={6} className={classes.rightBorder}>
                <Typography variant="h3" align="center">
                  {formatValue(stats.answersCount, assignment.studentsCount)}
                </Typography>
                <Typography variant="body2" gutterBottom align="center">
                  <FormattedMessage id="assignment.answers" />
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h3" align="center">
                  {formatValue(averageScore, maxScore)}
                </Typography>
                <Typography variant="body2" gutterBottom align="center">
                  <FormattedMessage id="assignment.averageScore" />
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        )}
      </CardActionArea>

      <Divider />
      <CardActions className={classes.actions}>
        <AssignmentListCardActions
          assignment={assignment}
          loading={loading}
          onToggleAssignment={onToggleAssignment}
          onRemoveAssignment={onRemoveAssignment}
          onSendAssignment={onSendAssignment}
          onEditAssignment={onEditAssignment}
        />
      </CardActions>
    </Card>
  );
};

AssignmentListCard.propTypes = {
  assignment: PropTypes.object,
  loading: PropTypes.bool,

  onToggleAssignment: PropTypes.func.isRequired,
  onSelectAssignment: PropTypes.func.isRequired,
  onRemoveAssignment: PropTypes.func.isRequired,
  onSendAssignment: PropTypes.func.isRequired,
  onEditAssignment: PropTypes.func.isRequired,
};

AssignmentListCard.defaultProps = {
  assignment: {},
  loading: false,
};

export default AssignmentListCard;
